import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AniLink from "gatsby-plugin-transition-link/AniLink";

import { breakpoints, colors } from "../../../theme";
import { ALL_RECOMMENDATIONS } from "../../utils/constants/recommendations";
import Button from "../../components/form/Button";
import useMediaQuery from "../../hooks/use-media-query";
import { safeWindow } from "../../utils/helpers/ssr";
import TestimonialCard from "./TestimonialCard";
import {
  CarouselRoot,
  CarouselViewport,
  CarouselTrack,
  CarouselItem,
  MasonryContainer,
  CardSlot,
  Controls,
  ArrowIcon,
  ArrowButton,
  LoadMoreContainer,
} from "./testimonials-layout-styles";

const MOBILE_VISIBLE_COUNT = 4;
const MOBILE_VISIBLE_INCREMENT = 4;

const reviewKey = (review, index) =>
  review.linkedIn || review.name || `review-${index}`;

const CarouselTestimonials = ({
  reviews = ALL_RECOMMENDATIONS,
  showMore = true,
  fullBleed = false,
}) => {
  const viewportRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.desktop})`);
  const useMarquee = isDesktop;

  const updateArrows = useCallback(() => {
    if (useMarquee) {
      setShowArrows(false);
      return;
    }
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    setShowArrows(viewport.scrollWidth > viewport.clientWidth + 1);
  }, [useMarquee]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    updateArrows();

    if (useMarquee) {
      return undefined;
    }

    const viewport = viewportRef.current;
    safeWindow.addEventListener("resize", updateArrows);
    if (viewport) {
      viewport.addEventListener("scroll", updateArrows);
    }

    return () => {
      safeWindow.removeEventListener("resize", updateArrows);
      if (viewport) {
        viewport.removeEventListener("scroll", updateArrows);
      }
    };
  }, [updateArrows, reviews, useMarquee]);

  const scrollByCard = (direction) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    const firstCard = viewport.querySelector("[data-testimonial-card]");
    const gap = 20;
    const cardWidth = firstCard
      ? firstCard.getBoundingClientRect().width + gap
      : viewport.clientWidth;
    viewport.scrollBy({ left: direction * cardWidth, behavior: "smooth" });
  };

  const trackItems = useMemo(() => {
    const base = reviews.map((review, index) => ({
      review,
      key: `${reviewKey(review, index)}-a`,
    }));
    if (!useMarquee) {
      return base;
    }
    return [
      ...base,
      ...reviews.map((review, index) => ({
        review,
        key: `${reviewKey(review, index)}-b`,
      })),
    ];
  }, [reviews, useMarquee]);

  const showArrowControls = !useMarquee && showArrows;
  const showControls = showMore || showArrowControls;

  return (
    <CarouselRoot data-testimonials $fullBleed={fullBleed}>
      <CarouselViewport ref={viewportRef} $marquee={useMarquee}>
        <CarouselTrack role="list" $marquee={useMarquee}>
          {trackItems.map(({ review, key }) => (
            <CarouselItem data-testimonial-card role="listitem" key={key}>
              <TestimonialCard review={review} expanded={false} />
            </CarouselItem>
          ))}
        </CarouselTrack>
      </CarouselViewport>

      {showControls && (
        <Controls>
          {showArrowControls && (
            <ArrowButton
              type="button"
              $prev
              aria-label="Previous testimonial"
              onClick={() => scrollByCard(-1)}
            >
              <ArrowIcon $prev aria-hidden />
            </ArrowButton>
          )}

          {showMore && (
            <Button
              as={AniLink}
              cover
              bg={colors.black}
              to="/testimonials/"
              variant="outlined"
              style={{ lineHeight: "18px" }}
            >
              More testimonials
            </Button>
          )}

          {showArrowControls && (
            <ArrowButton
              type="button"
              aria-label="Next testimonial"
              onClick={() => scrollByCard(1)}
            >
              <ArrowIcon aria-hidden />
            </ArrowButton>
          )}
        </Controls>
      )}
    </CarouselRoot>
  );
};

const MasonryTestimonials = () => {
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.desktop})`);
  const [visibleCount, setVisibleCount] = useState(MOBILE_VISIBLE_COUNT);

  const canLoadMore = !isDesktop && visibleCount < ALL_RECOMMENDATIONS.length;

  const handleLoadMore = () => {
    setVisibleCount((count) => count + MOBILE_VISIBLE_INCREMENT);
  };

  return (
    <>
      <MasonryContainer role="list">
        {ALL_RECOMMENDATIONS.map((review, index) => (
          <CardSlot
            role="listitem"
            key={index}
            $hiddenOnMobile={index >= visibleCount}
          >
            <TestimonialCard review={review} expanded />
          </CardSlot>
        ))}
      </MasonryContainer>

      {canLoadMore && (
        <LoadMoreContainer>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            style={{ lineHeight: "18px" }}
          >
            More testimonials
          </Button>
        </LoadMoreContainer>
      )}
    </>
  );
};

const Testimonials = ({
  expanded = false,
  reviews = ALL_RECOMMENDATIONS,
  showMore = true,
  fullBleed = false,
}) => {
  return expanded ? (
    <MasonryTestimonials />
  ) : (
    <CarouselTestimonials
      reviews={reviews}
      showMore={showMore}
      fullBleed={fullBleed}
    />
  );
};

export default Testimonials;
